import { useCallback, useMemo, useState, type ReactNode } from "react";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { RequestError } from "@/app/queries";
import { useAssignMentor, useCohorts, useCreateCohort, useInviteBatch, useInvites, useMentorLoad, usePeople, useSetRole, type Cohort, type Invite2, type MentorLoad, type Person2 } from "@/app/peopleQueries";
import { useMe } from "@/app/session";
import { Button } from "@/ui/Button";
import { EmptyState, NoAccess, SkeletonRows, StateCard, Toast } from "@/ui/states";
import { BulkBar } from "./admin/people/BulkBar";
import { FactsCard, MentorLoadCard, PermsCard } from "./admin/people/Cards";
import { InviteModal, type InviteBody } from "./admin/people/InviteModal";
import { PeopleTable } from "./admin/people/PeopleTable";
import { CohortCards, Eyebrow, InvitesList, NewCohortForm, RoleFilters } from "./admin/people/Sidebar";
import { INPUT_CLS, SELECT_CLS } from "./admin/people/shared";
import { filterRows, isMentor, scopeTitle, subtitle, type MentorFilter, type Role, type RoleFilter, type Scope } from "./admin/people/model";

export const NO_ACCESS_BODY = "Людей и когорты видят администраторы и владелец школы. Если вам нужно управлять людьми — попросите роль у администратора.";

const ADMIN = new Set(["admin", "owner"]);

/** A2 «Люди и когорты»: карточки когорт — переключатель режима, таблица людей с
 *  фильтрами и пачечным назначением ментора, приглашения, нагрузка менторов, права. */
export function PeoplePage() {
  const me = useMe();
  const [scope, setScope] = useState<Scope>("all");
  // Роль и поиск переживают смену режима; выбор и фильтр ментора — нет (спека §3.2).
  const [role, setRole] = useState<RoleFilter>("all");
  const [query, setQuery] = useState("");
  const cohorts = useCohorts();
  const peopleQ = usePeople({ scope });
  const mentorsQ = usePeople({ scope: "all", role: "mentor" });
  const load = useMentorLoad(scope);
  const invites = useInvites();

  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const frame = (children: ReactNode) => (
    <Shell nav user={user}>
      {children}
    </Shell>
  );

  const queries = [cohorts, peopleQ, mentorsQ, load, invites];
  const err = queries.map((q) => q.error).find(Boolean);
  const forbidden = (me.data && !ADMIN.has(me.data.role)) || (err instanceof RequestError && err.status === 403);
  if (forbidden) return frame(<NoAccess where="люди и когорты" body={NO_ACCESS_BODY} primary={{ label: "на мой маршрут", href: "/" }} />);
  if (err)
    return frame(
      <StateCard
        code="ERR_NETWORK"
        where="люди и когорты"
        tone="warn"
        title="Нет связи с сервером"
        body="Список людей не загрузился. Назначения и приглашения хранятся на сервере — ничего не потеряно."
        primary={{ label: "повторить сейчас", onClick: () => queries.forEach((q) => void q.refetch()) }}
      />,
    );
  if (!cohorts.data || !peopleQ.data || !mentorsQ.data || !load.data || !invites.data || !me.data) return frame(<SkeletonRows />);
  return (
    <People
      key={scope}
      scope={scope}
      onScope={setScope}
      role={role}
      onRole={setRole}
      query={query}
      onQuery={setQuery}
      me={me.data}
      user={user}
      rows={peopleQ.data}
      mentors={mentorsQ.data.filter(isMentor)}
      cohorts={cohorts.data.cohorts}
      noCohort={cohorts.data.no_cohort}
      load={load.data}
      invites={invites.data}
    />
  );
}

type Props = {
  scope: Scope;
  onScope: (s: Scope) => void;
  role: RoleFilter;
  onRole: (r: RoleFilter) => void;
  query: string;
  onQuery: (q: string) => void;
  me: { user_id: string; role: string };
  user?: { name: string; role: string };
  rows: Person2[];
  mentors: Person2[];
  cohorts: Cohort[];
  noCohort: number;
  load: MentorLoad;
  invites: Invite2[];
};

/* key={scope} выше: смена режима пересоздаёт локальное состояние — выбор и фильтр ментора сбрасываются. */
function People({ scope, onScope, role, onRole: setRole, query, onQuery: setQuery, me, user, rows, mentors, cohorts, noCohort, load, invites }: Props) {
  const [mentor, setMentor] = useState<MentorFilter>("");
  const [sel, setSel] = useState<ReadonlySet<string>>(new Set());
  const [inviteOpen, setInviteOpen] = useState(false);
  const [flash, setFlash] = useState<{ tone: "err" | "ok"; text: string }>();

  const assign = useAssignMentor();
  const setRoleM = useSetRole();
  const createCohort = useCreateCohort();
  const inviteBatch = useInviteBatch();
  const busy = assign.isPending || setRoleM.isPending;

  const visible = useMemo(() => filterRows(rows, role, mentor, query), [rows, role, mentor, query]);
  // Пачка — только видимые выбранные: скрытые поиском строки не трогаем.
  const selected = useMemo(() => visible.filter((p) => sel.has(p.id)), [visible, sel]);
  const cohort = cohorts.find((c) => c.id === scope);
  const closeInvite = useCallback(() => setInviteOpen(false), []);

  const fail = (e: unknown) => setFlash({ tone: "err", text: e instanceof Error ? e.message : "Не удалось выполнить действие." });
  const doAssign = (students: string[], mentorId: string | undefined) => {
    setFlash(undefined);
    assign.mutate({ students, mentor_id: mentorId }, { onSuccess: () => setSel(new Set()), onError: fail });
  };
  const doRole = (userId: string, r: Role) => {
    setFlash(undefined);
    setRoleM.mutate({ userId, role: r }, { onError: fail });
  };
  const sendInvites = (b: InviteBody) => inviteBatch.mutateAsync(b);

  return (
    <Shell nav wide user={user}>
      <div className="grid grid-cols-1 lg:grid-cols-[272px_minmax(0,1fr)]">
        <aside className="flex flex-col gap-4 border-bd px-4 py-[18px] lg:border-r">
          <section className="flex flex-col gap-2.5">
            <Eyebrow>когорты</Eyebrow>
            <CohortCards cohorts={cohorts} noCohort={noCohort} scope={scope} onScope={onScope} />
            <NewCohortForm onCreate={(b) => createCohort.mutateAsync(b).then((c) => onScope(c.id))} pending={createCohort.isPending} error={createCohort.error?.message} />
          </section>
          <section className="border-t border-bd pt-3.5">
            <Eyebrow>роли</Eyebrow>
            <RoleFilters rows={rows} value={role} onChange={(r) => (setRole(r), setSel(new Set()))} />
          </section>
          <section className="border-t border-bd pt-3.5">
            <Eyebrow>приглашения</Eyebrow>
            <InvitesList invites={invites} />
          </section>
        </aside>

        <div className="min-w-0">
          <div className="flex flex-wrap items-start gap-3.5 px-5 pt-[18px]">
            <div className="min-w-0 flex-1">
              <h1 className="m-0 font-mono text-[22px] font-bold tracking-[-.015em]">{scopeTitle(scope, cohorts)}</h1>
              <p className="m-0 mt-1 font-mono text-[12px] text-mut" data-testid="subtitle">
                {subtitle(scope, rows)}
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setInviteOpen(true)} data-testid="invite-open">
              пригласить
            </Button>
          </div>

          <div className="mt-3.5 flex flex-wrap items-center gap-3 px-5">
            <input type="search" aria-label="поиск по людям" placeholder="фамилия, email или когорта" value={query} onChange={(e) => setQuery(e.target.value)} className={`${INPUT_CLS} w-[260px] max-w-full`} />
            <label className="flex items-center gap-2 font-mono text-[11.5px] text-mut">
              ментор
              <select aria-label="фильтр по ментору" data-testid="mentor-filter" value={mentor} onChange={(e) => (setMentor(e.target.value), setSel(new Set()))} className={SELECT_CLS}>
                <option value="">все</option>
                <option value="none">без ментора</option>
                {mentors.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="hidden font-mono text-[11px] text-mut xl:inline">ментор меняется в строке · для нескольких сразу — отметь чекбоксы</span>
            <span className="flex-1" />
            <span className="font-mono text-[11.5px] text-mut" data-testid="shown-label">
              показано {visible.length} из {rows.length}
            </span>
          </div>

          {flash && (
            <div className="px-5 pt-3" data-testid="action-error">
              <Toast tone={flash.tone} title={flash.tone === "err" ? "Не получилось" : "Готово"} body={flash.text} action={{ label: "закрыть", onClick: () => setFlash(undefined) }} />
            </div>
          )}

          <BulkBar selected={selected} mentors={mentors} busy={busy} onClear={() => setSel(new Set())} onAssign={(mid) => doAssign(selected.filter((p) => p.role === "student").map((p) => p.id), mid)} />

          <div className="mt-3">
            {rows.length === 0 ? (
              <div className="px-5 pb-2">
                <EmptyState
                  where="люди"
                  title={scope === "nocohort" ? "Вне когорт никого нет" : scope === "all" ? "В школе пока никого нет" : "В когорте пока никого нет"}
                  body={scope === "nocohort" ? "Все студенты распределены по когортам. Студента без общего старта можно пригласить сюда — он будет вестись индивидуально." : "Пригласите людей письмом — пока они не примут приглашение, они висят в списке «приглашения» как «ожидает»."}
                  marks={[true, false, false]}
                  primary={{ label: "пригласить", onClick: () => setInviteOpen(true) }}
                />
              </div>
            ) : (
              <>
                <PeopleTable
                  rows={visible}
                  mentors={mentors}
                  selected={sel}
                  onToggle={(id) =>
                    setSel((s) => {
                      const n = new Set(s);
                      if (n.has(id)) n.delete(id);
                      else n.add(id);
                      return n;
                    })
                  }
                  onToggleAll={(ids, on) =>
                    setSel((s) => {
                      const n = new Set(s);
                      ids.forEach((id) => (on ? n.add(id) : n.delete(id)));
                      return n;
                    })
                  }
                  onAssign={(id, mid) => doAssign([id], mid)}
                  onRole={doRole}
                  viewerRole={me.role}
                  viewerId={me.user_id}
                  busy={busy}
                />
                {visible.length === 0 && (
                  <div className="flex flex-col items-center gap-3 px-5 py-10" data-testid="filter-empty">
                    <span className="font-mono text-[12.5px] text-mut">никого не нашлось</span>
                    <Button size="sm" onClick={() => (setRole("all"), setMentor(""), setQuery(""))}>
                      сбросить фильтры и поиск
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3.5 px-5 pb-6 pt-[18px] md:grid-cols-3">
            <MentorLoadCard load={load} />
            <PermsCard />
            <FactsCard scope={scope} cohort={cohort} rows={rows} invites={invites} />
          </div>
        </div>
      </div>
      <InviteModal open={inviteOpen} onClose={closeInvite} cohorts={cohorts} mentors={mentors} defaultCohortId={cohort?.id} onSubmit={sendInvites} pending={inviteBatch.isPending} />
    </Shell>
  );
}
